import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";
import {
  getOrganizations,
  createOrganization,
  getOrganizationById,
  updateOrganization,
} from "services/organization.service";
import { handleAPIError, queryClient } from "utils";
import { Organization } from "types/organization";

// Fetch all organizations
export const useGetOrganizations = () => {
  return useQuery({
    queryKey: [queryKeys.organizations],
    queryFn: async () => await getOrganizations(),
  });
};

//fetch organization by id
export const useGetOrganizationsById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.organizations, id], // Unique key for fetching a single organization
    queryFn: async () => await getOrganizationById(id),
    enabled: !!id, // Only run query if ID is provided
  });
};

// Define the UpdateOrganizationsPayload type
export type UpdateOrganizationsPayload = {
  // Required branch ID
  id: number | string;
  data: Organization["org"];
};

// Hook to update an Organization
export const useUpdateOrganizations = (): UseMutationResult<
  any,
  Error,
  UpdateOrganizationsPayload
> => {
  return useMutation<any, Error, UpdateOrganizationsPayload>({
    mutationFn: async ({ id, data }) => {
      return updateOrganization(id, data);
    },
    onSuccess: async () => {
      // Invalidate Organizations queries on success to refresh the lists
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes(queryKeys.organizations),
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Create a new organization
export const useCreateOrganization = () => {
  return useMutation({
    mutationFn: createOrganization,
    onSuccess: async () => {
      // Invalidate organizations query on success to refresh the list
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.organizations],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
